import { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Trash2, GraduationCap, Rocket, Users, PartyPopper, Calendar, Pencil } from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  capacitacao: { label: "Capacitação", icon: GraduationCap, color: "hsl(var(--sky))" },
  lancamento: { label: "Lançamento", icon: Rocket, color: "hsl(var(--gold))" },
  reuniao: { label: "Reunião", icon: Users, color: "hsl(21 90% 48%)" },
  celebracao: { label: "Celebração", icon: PartyPopper, color: "hsl(var(--emerald))" },
};

const EMPTY_EVENT = { title: "", description: "", date: "", time: "19:30", type: "reuniao", phase: "" };

function EventForm({ initial, onSaved }) {
  const isEdit = !!initial;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial ? { ...EMPTY_EVENT, ...initial } : EMPTY_EVENT);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { if (open) setForm(initial ? { ...EMPTY_EVENT, ...initial } : EMPTY_EVENT); }, [open, initial]);

  const save = async (e) => {
    e.preventDefault();
    const payload = { title: form.title, description: form.description, date: form.date, time: form.time, type: form.type, phase: form.phase || null };
    try {
      if (isEdit) await api.put(`/events/${initial.id}`, payload);
      else await api.post("/events", payload);
      toast.success(isEdit ? "Evento atualizado" : "Evento criado");
      setOpen(false);
      if (!isEdit) setForm(EMPTY_EVENT);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit
          ? <Button variant="ghost" size="icon" className="mt-1 h-7 w-7" data-testid={`edit-event-${initial.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
          : <Button className="gap-2" data-testid="calendar-add-event-button"><Plus className="h-4 w-4" /> Novo Evento</Button>}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{isEdit ? "Editar Evento" : "Novo Evento"}</DialogTitle>
          <DialogDescription>Preencha os dados do evento do cronograma.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div>
            <Label>Título</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} required className="mt-1.5" data-testid="event-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Data</Label>
              <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} required className="mt-1.5" data-testid="event-date-input" />
            </div>
            <div>
              <Label>Horário</Label>
              <Input value={form.time} onChange={(e) => set("time", e.target.value)} className="mt-1.5" />
            </div>
          </div>
          <div>
            <Label>Tipo</Label>
            <Select value={form.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TYPE_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Descrição</Label>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5" />
          </div>
          <DialogFooter><Button type="submit" data-testid="save-event-button">{isEdit ? "Salvar" : "Criar"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function CalendarPage() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const canEdit = !!user && user.role !== "membro" && user.role !== "visitante";

  const load = () => api.get("/events").then((r) => setEvents(r.data));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    await api.delete(`/events/${id}`);
    toast.success("Evento removido");
    load();
  };

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = events.filter((e) => e.date >= today);
  const past = events.filter((e) => e.date < today);

  const renderEvent = (e) => {
    const meta = TYPE_META[e.type] || TYPE_META.reuniao;
    const Icon = meta.icon;
    return (
      <div key={e.id} className="relative pl-10 pb-8 last:pb-0" data-testid="timeline-phase-item">
        <span className="absolute left-0 top-0 h-8 w-8 rounded-full flex items-center justify-center border-2" style={{ borderColor: meta.color, color: meta.color, background: "hsl(var(--card))" }}>
          <Icon className="h-4 w-4" />
        </span>
        <div className="absolute left-[15px] top-8 bottom-0 w-px bg-border" />
        <Card className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-display font-semibold">{e.title}</h4>
                <Badge variant="secondary" style={{ color: meta.color }}>{meta.label}</Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-1">{e.description}</p>
              {e.phase && <p className="text-xs text-muted-foreground mt-1.5 font-medium">{e.phase}</p>}
            </div>
            <div className="text-right shrink-0">
              <div className="text-sm font-semibold">{new Date(e.date + "T00:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })}</div>
              <div className="text-xs text-muted-foreground">{e.time}</div>
              {canEdit && (
                <div className="flex items-center justify-end gap-1 mt-1">
                  <EventForm initial={e} onSaved={load} />
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => del(e.id)} data-testid={`delete-event-${e.id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>
    );
  };

  return (
    <div className="space-y-8 animate-fade-up" data-testid="calendar-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Calendário & Cronograma</h1>
          <p className="text-muted-foreground mt-1">Reuniões, capacitações e fases de implantação dos PGDs.</p>
        </div>
        {canEdit && <EventForm onSaved={load} />}
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          <h2 className="font-display text-xl font-semibold mb-6 flex items-center gap-2"><Calendar className="h-5 w-5 text-primary" /> Próximos eventos</h2>
          <div>{upcoming.length ? upcoming.map(renderEvent) : <p className="text-muted-foreground text-sm">Nenhum evento agendado.</p>}</div>
        </div>
        <div>
          <h2 className="font-display text-xl font-semibold mb-6 text-muted-foreground">Histórico</h2>
          <div className="opacity-70">{past.length ? past.map(renderEvent) : <p className="text-muted-foreground text-sm">Sem eventos anteriores.</p>}</div>
        </div>
      </div>
    </div>
  );
}
